import { formatInTimeZone } from 'date-fns-tz';

import { useFeatureFlag } from '@order/LaunchDarkly';

/**
 * Returns the URL for the Momo survey.
 * Note that the `cels-4626-momo-rate-order-survey-enabled` flag contains either "Disabled" or the survey URL.
 * @see {@link https://go-momos.notion.site/Survey-URL-Query-Params-1ecef141e357801e8bb2c428c5b36755}
 */
export const useMomoSurveyUrl = (params: MomoSurveyUrlParams) => {
  const {
    orderId,
    orderTime,
    isInStoreOrder,
    restaurantEntityId,
    customerId,
    firstName,
    lastName,
    email,
  } = params;

  const momoSurveyUrl = useFeatureFlag('cels-4626-momo-rate-order-survey-enabled'); // prettier-ignore
  const restaurantEntityIdMapping = useFeatureFlag('cels-4651-momo-poc-restaurant-entity-id-mapping'); // prettier-ignore

  const isDisabled = momoSurveyUrl === disabled;
  const outletId = restaurantEntityIdMapping?.[restaurantEntityId]; // prettier-ignore

  if (!isInStoreOrder || isDisabled || !outletId) return null;

  const url = new URL(momoSurveyUrl);
  url.searchParams.set('serviceType', serviceType);
  url.searchParams.set('outletId', outletId);
  url.searchParams.set('name', getCustomerName(firstName, lastName));
  url.searchParams.set('email', email ?? '');
  url.searchParams.set('phoneNumber', customerId ?? '');
  url.searchParams.set('orderId', orderId);
  url.searchParams.set('orderTime', toMomoTimestamp(orderTime));

  return url.toString();
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getCustomerName = (firstName = '', lastName = '') => `${firstName} ${lastName}`.trim();

/**
 * Date and time of the order in the format YYYY-MM-DDThh24:mi:ssZ
 */
export function toMomoTimestamp(date: string) {
  return formatInTimeZone(new Date(date), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'");
}

// ─── Constants ───────────────────────────────────────────────────────────────

const disabled = 'Disabled' as const;
const serviceType = 'dinein'; // cspell:disable-line

// ─── Types ───────────────────────────────────────────────────────────────────

type MomoSurveyUrlParams = {
  orderId: string;
  orderTime: string;
  isInStoreOrder: boolean;
  restaurantEntityId: string;
  customerId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};
